import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
} from 'recharts';

export default function InvestorPortfolioAnalytics() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<any[]>([]);
  const [trend, setTrend] = useState<any[]>([]);
  const [risk, setRisk] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);

  async function load() {
    if (!user) return;

    try {
      setLoading(true);

      const [
        { data: ov, error: ovError },
        { data: tr, error: trError },
        { data: rk, error: rkError },
      ] = await Promise.all([
        supabase.rpc('investor_portfolio_overview', { p_user_id: user.id }),
        supabase.rpc('investor_portfolio_trend', { p_user_id: user.id }),
        supabase.rpc('investor_portfolio_risk', { p_user_id: user.id }),
      ]);

      if (ovError) throw ovError;
      if (trError) throw trError;
      if (rkError) throw rkError;

      setOverview(ov || []);
      setTrend(tr || []);
      setRisk(rk || []);
      const totalVal = (ov || []).reduce((s: number, r: any) => s + (r.implied_value || 0), 0);
      setTotal(totalVal);
    } catch (e: any) {
      toast.error(e.message || 'Failed to load portfolio data');
      console.error('Portfolio load error:', e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [user]);

  const riskChart = useMemo(() => {
    return (risk || []).map((r: any) => ({ name: r.company_name, score: r.risk_score || 0 }));
  }, [risk]);

  async function exportToPDF() {
    if (!user) return;

    try {
      toast.loading('Generating PDF...');

      const { data, error } = await supabase.functions.invoke('export-portfolio-pdf', {
        body: { userId: user.id },
      });

      if (error) throw error;

      // Create downloadable HTML file (browser will handle PDF conversion via print)
      const blob = new Blob([data.html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `portfolio-${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Portfolio exported! Open the file and print to PDF.');
    } catch (err: any) {
      console.error('PDF export error:', err);
      toast.error(err.message || 'Failed to export portfolio');
    }
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 space-y-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold tracking-tight">Portfolio Analytics</h1>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={exportToPDF}>
              <Download className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
            <Button variant="outline" size="sm" onClick={load}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid sm:grid-cols-3 gap-4">
          <MetricCard title="Total Implied Value" value={`R${total.toLocaleString()}`} />
          <MetricCard title="Companies" value={overview.length} />
          <MetricCard
            title="Avg Risk Score"
            value={avg(risk.map((r: any) => r.risk_score || 0)).toFixed(1)}
          />
        </div>

        {/* 30-day Value Trend */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle>Portfolio Value (30 days)</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend}>
                <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--background))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '0.5rem',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="implied_value"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  name="Value"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Company Risk Bar */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle>Company Risk Scores</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={riskChart}>
                <XAxis dataKey="name" hide />
                <YAxis domain={[0, 100]} stroke="hsl(var(--muted-foreground))" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--background))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '0.5rem',
                  }}
                />
                <Bar dataKey="score" fill="hsl(var(--destructive))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Positions Table */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle>Positions</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Shares</TableHead>
                  <TableHead>FMV / Share</TableHead>
                  <TableHead className="text-right">Implied Value</TableHead>
                  <TableHead className="text-right">Latest Report</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {overview.map((r: any) => (
                  <TableRow key={r.company_id}>
                    <TableCell className="font-medium">{r.company_name}</TableCell>
                    <TableCell>{r.shares?.toLocaleString?.() ?? r.shares}</TableCell>
                    <TableCell>
                      {r.fmv_per_share ? `R${r.fmv_per_share.toFixed(4)}` : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {r.implied_value ? `R${r.implied_value.toLocaleString()}` : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {r.last_report_url ? (
                        <a
                          href={r.last_report_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          Open
                        </a>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

function MetricCard({ title, value }: { title: string; value: string | number }) {
  return (
    <Card className="border-border bg-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}

function avg(arr: number[]) {
  if (!arr.length) return 0;
  return arr.reduce((s, n) => s + n, 0) / arr.length;
}
